/**
 * WidgetRenderer — maps dashboard widget types to visualization components.
 * Transforms raw marketing API data into the props each chart expects.
 */

import type { DashboardWidget, WidgetConfig } from '@/types/dashboard'
import type {
  AnalyticsOverview,
  Campaign,
  CampaignPerformanceRow,
  AttributionFunnelStep,
  TimeSeriesPoint,
  OptimizationAction,
  ConversionEvent,
} from '@/types/marketing'

import { AttributionFunnel } from '../visualizations/AttributionFunnel'
import { PerformanceTrends } from '../visualizations/PerformanceTrends'
import { CampaignComparison } from '../visualizations/CampaignComparison'
import { CampaignHealthRadar } from '../visualizations/CampaignHealthRadar'
import { SpendRoasScatter } from '../visualizations/SpendRoasScatter'
import { BudgetSankey } from '../visualizations/BudgetSankey'
import { JourneyTimeline } from '../visualizations/JourneyTimeline'
import { buildComparisonBarData, buildJourneyEvents, buildSankeyData } from './widgetTransforms'
import { formatMarketingCents } from '../format'
import { useMarketingStore } from '@/stores/marketingStore'
import { cn } from '@/utils'
import type { MarketingChannel } from '@/types/marketing'
import {
  DollarSign,
  Eye,
  MousePointer,
  MessageSquare,
  Users,
  TrendingUp,
  Zap,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { Badge, Button, Spinner } from '@/components/ui'

/** All the marketing data a widget might need */
export interface WidgetDataBag {
  overview?: AnalyticsOverview | null
  campaigns?: Campaign[]
  campaignPerformance?: CampaignPerformanceRow[]
  attributionFunnel?: AttributionFunnelStep[]
  /** Completed in-window orders with no resolvable campaign attribution
   * (unfiltered funnel view only; null/absent = annotation not shown). */
  attributionUnattributed?: number | null
  timeSeries?: TimeSeriesPoint[]
  optimizationActions?: OptimizationAction[]
  conversions?: ConversionEvent[]
  /** The dashboard's global date range, for widgets that scope client-side. */
  dateRangeDays?: number
  isLoading?: boolean
}

interface WidgetRendererProps {
  widget: DashboardWidget
  data: WidgetDataBag
  height: number
  onReviewAction?: (actionId: string, decision: 'approve' | 'dismiss') => void
}

const fmtCurrency = formatMarketingCents

function fmtNumber(n: number): string {
  return n.toLocaleString()
}

function fmtCtr(ctrTimes10000: number): string {
  return `${(ctrTimes10000 / 10000).toFixed(2)}%`
}

export function WidgetRenderer({ widget, data, height, onReviewAction }: WidgetRendererProps) {
  // Cross-widget campaign selection — shared marketingStore state, like the
  // channel filter. Campaign rows toggle it; scoped charts can clear it.
  const selectedCampaignIds = useMarketingStore((s) => s.selectedCampaignIds)
  const toggleCampaignId = useMarketingStore((s) => s.toggleCampaignId)
  const clearSelectedCampaigns = useMarketingStore((s) => s.clearSelectedCampaigns)
  const selectedChannel = useMarketingStore((s) => s.selectedChannel)

  if (data.isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    )
  }

  switch (widget.type) {
    case 'kpi_card':
      return <KpiCardWidget overview={data.overview} config={widget.config} channel={selectedChannel} />

    case 'attribution_funnel': {
      const unattributed = data.attributionUnattributed ?? null
      const showUnattributed = unattributed != null && unattributed > 0
      return (
        <div>
          <AttributionFunnel
            data={(data.attributionFunnel || []).map((step) => ({
              id: step.label.toLowerCase().replace(/\s+/g, '_'),
              label: step.label,
              value: step.count,
              cost: step.cost,
              untracked: step.untracked,
            }))}
            height={height - (showUnattributed ? 34 : 16)}
            // The funnel shows the selection-scoped pipeline; steps carry no
            // campaign identity, so a click un-scopes back to all campaigns.
            onStepClick={selectedCampaignIds.length > 0 ? clearSelectedCampaigns : undefined}
          />
          {showUnattributed && (
            <p className="mt-1 text-xs text-muted-foreground">
              {unattributed.toLocaleString()} completed {unattributed === 1 ? 'order' : 'orders'} not attributed to a campaign
            </p>
          )}
        </div>
      )
    }

    case 'performance_trends':
      return (
        <PerformanceTrends
          data={(data.timeSeries || []) as any}
          metrics={(widget.config.metrics as string[]) || ['spend', 'clicks', 'conversations']}
          height={height - 16}
          yFormat="number"
          enableArea={(widget.config.enableArea as boolean) ?? false}
        />
      )

    case 'campaign_comparison': {
      const { data: barData, labelToId } = buildComparisonBarData(data.campaignPerformance || [])
      return (
        <CampaignComparison
          data={barData}
          metrics={(widget.config.metrics as string[]) || ['spend']}
          height={height - 16}
          valueFormat="currency"
          onBarClick={(campaignLabel) => {
            const id = labelToId.get(campaignLabel)
            if (id) toggleCampaignId(id)
          }}
        />
      )
    }

    case 'campaign_health_radar': {
      const perf = data.campaignPerformance || []
      const radarData = perf
        .filter((r) => r.status === 'ACTIVE')
        .slice(0, 4)
        .map((r) => {
          const ctr = r.impressions > 0 ? (r.clicks / r.impressions) * 100 : 0
          const convRate = r.clicks > 0 ? (r.conversations / r.clicks) * 100 : 0
          const costEff = 100 - Math.min(r.leads > 0 ? r.spend / r.leads / 100 : 100, 100)
          const roas = r.spend > 0 ? ((r.purchases || 0) * 100) / r.spend : 0
          return {
            campaign: r.name.length > 15 ? r.name.slice(0, 13) + '...' : r.name,
            metrics: [
              { metric: 'CTR', value: Math.min(ctr * 20, 100), rawValue: `${ctr.toFixed(2)}%` },
              { metric: 'Conv. Rate', value: Math.min(convRate * 10, 100), rawValue: `${convRate.toFixed(1)}%` },
              { metric: 'Cost Eff.', value: Math.max(costEff, 0), rawValue: `${fmtCurrency(r.costPerLead)} CPL` },
              { metric: 'ROAS', value: Math.min(roas * 25, 100), rawValue: `${roas.toFixed(2)}x` },
              { metric: 'Volume', value: Math.min(r.impressions / 1000, 100), rawValue: fmtNumber(r.impressions) },
              { metric: 'Engagement', value: Math.min(ctr * 10, 100), rawValue: `${ctr.toFixed(2)}% CTR` },
            ],
          }
        })
      return <CampaignHealthRadar data={radarData} height={height - 16} />
    }

    case 'budget_sankey':
      return <BudgetSankey data={buildSankeyData(data.campaignPerformance || [])} height={height - 16} />

    case 'journey_timeline':
      return (
        <JourneyTimeline
          events={buildJourneyEvents(data.conversions || [], { sinceDays: data.dateRangeDays })}
          height={height - 16}
        />
      )

    case 'demographic_heatmap':
      // Demographic breakdowns are not fetched by the insights sync, so there
      // is no real age/gender data to chart — say so instead of faking it.
      return (
        <div className="flex items-center justify-center h-full text-sm text-muted-foreground text-center px-4">
          Demographic breakdowns aren't synced yet
        </div>
      )

    case 'spend_roas_scatter': {
      const perf = data.campaignPerformance || []
      const points = perf.map((r) => ({
        id: r.name,
        spend: r.spend,
        roas: r.spend > 0 ? Math.round(((r.purchases || 0) / r.spend) * 100) / 100 : 0,
        conversions: r.conversations + r.leads,
        status: r.status as 'ACTIVE' | 'PAUSED' | 'ARCHIVED',
      }))
      return <SpendRoasScatter data={points} height={height - 16} />
    }

    case 'active_campaigns_list': {
      const active = (data.campaigns || []).filter((c) => c.status === 'ACTIVE')
      if (active.length === 0) {
        return <div className="flex items-center justify-center h-full text-sm text-muted-foreground">No active campaigns</div>
      }
      return (
        <div className="overflow-y-auto h-full px-1">
          <div className="divide-y divide-border/60">
            {active.map((campaign) => {
              const selected = selectedCampaignIds.includes(campaign.id)
              return (
                <button
                  key={campaign.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleCampaignId(campaign.id)}
                  className={cn(
                    'w-full text-left py-2.5 px-1.5 flex items-center justify-between cursor-pointer transition-colors',
                    selected ? 'bg-primary/10 hover:bg-primary/15' : 'hover:bg-muted/50',
                  )}
                >
                  {/* spans, not <p>/<div>: buttons allow phrasing content only */}
                  <span className="block min-w-0">
                    <span className="block font-medium text-sm truncate">{campaign.name}</span>
                    <span className="block text-xs text-muted-foreground truncate">
                      {campaign.objective} &middot;{' '}
                      {campaign.dailyBudget ? `${fmtCurrency(campaign.dailyBudget)}/day` : 'No daily budget'}
                    </span>
                  </span>
                  <Badge variant="success" className="ml-2 shrink-0">ACTIVE</Badge>
                </button>
              )
            })}
          </div>
        </div>
      )
    }

    case 'optimization_actions': {
      const pending = (data.optimizationActions || []).filter((a) => a.status === 'recommended')
      if (pending.length === 0) {
        return <div className="flex items-center justify-center h-full text-sm text-muted-foreground">No pending actions</div>
      }
      return (
        <div className="overflow-y-auto h-full px-1">
          <div className="divide-y divide-border/60">
            {pending.map((action) => (
              <div key={action.id} className="py-2.5 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{action.description || action.actionType}</p>
                    <p className="text-xs text-muted-foreground">
                      {action.entityType} &middot; Confidence: {action.confidence}%
                    </p>
                  </div>
                  {onReviewAction && (
                    <div className="flex gap-1.5 shrink-0">
                      <Button variant="secondary" size="sm" onClick={() => onReviewAction(action.id, 'dismiss')}>
                        <XCircle className="w-3 h-3" />
                      </Button>
                      <Button size="sm" onClick={() => onReviewAction(action.id, 'approve')}>
                        <CheckCircle className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    case 'campaign_performance_table': {
      const rows = data.campaignPerformance || []
      if (rows.length === 0) {
        return <div className="flex items-center justify-center h-full text-sm text-muted-foreground">No campaign data</div>
      }
      return (
        <div className="overflow-auto h-full">
          {/* role=grid: selectable rows — aria-selected is invalid on plain table rows */}
          <table role="grid" aria-multiselectable="true" className="w-full text-sm">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b text-left">
                <th className="pb-2 pt-1 font-medium">Campaign</th>
                <th className="pb-2 pt-1 font-medium text-right">Spend</th>
                <th className="pb-2 pt-1 font-medium text-right">Clicks</th>
                <th className="pb-2 pt-1 font-medium text-right">Conv.</th>
                <th className="pb-2 pt-1 font-medium text-right">Leads</th>
                <th className="pb-2 pt-1 font-medium text-right">CPL</th>
                <th className="pb-2 pt-1 font-medium text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((row) => {
                const selected = selectedCampaignIds.includes(row.campaignId)
                return (
                <tr
                  key={row.campaignId}
                  tabIndex={0}
                  aria-selected={selected}
                  onClick={() => toggleCampaignId(row.campaignId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      toggleCampaignId(row.campaignId)
                    }
                  }}
                  className={cn('cursor-pointer', selected ? 'bg-primary/10 hover:bg-primary/15' : 'hover:bg-muted')}
                >
                  <td className="py-2 font-medium truncate max-w-[200px]">{row.name}</td>
                  <td className="py-2 text-right">{fmtCurrency(row.spend)}</td>
                  <td className="py-2 text-right">{fmtNumber(row.clicks)}</td>
                  <td className="py-2 text-right">{fmtNumber(row.conversations)}</td>
                  <td className="py-2 text-right">{fmtNumber(row.leads)}</td>
                  <td className="py-2 text-right">{row.costPerLead > 0 ? fmtCurrency(row.costPerLead) : '—'}</td>
                  <td className="py-2 text-right">
                    <Badge variant={row.status === 'ACTIVE' ? 'success' : 'secondary'}>{row.status}</Badge>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    }

    default:
      return (
        <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
          Widget type "{widget.type}" not yet implemented
        </div>
      )
  }
}

/** KPI card widget — shows a single metric from the overview */
function KpiCardWidget({ overview, config, channel }: { overview?: AnalyticsOverview | null; config: WidgetConfig; channel?: MarketingChannel | null }) {
  const metric = (config.kpiMetric as string) || 'totalSpend'

  if (!overview) {
    return <div className="flex items-center justify-center h-full text-sm text-muted-foreground">No data</div>
  }

  // Google campaigns have no click→conversation link, so the API's 0
  // purchases means "not tracked", not zero sales — mirror the funnel's
  // untracked treatment instead of showing a dishonest number.
  if (metric === 'totalPurchases' && channel === 'google') {
    return (
      <div className="flex flex-col justify-center h-full px-1">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground uppercase tracking-wide">Purchases</span>
        </div>
        <p className="text-2xl font-bold italic text-muted-foreground">n/a</p>
        <p className="text-xs text-muted-foreground mt-0.5">Not tracked for this channel</p>
      </div>
    )
  }

  const metrics: Record<string, { icon: any; label: string; value: string; sub?: string }> = {
    totalSpend: { icon: DollarSign, label: 'Total Spend', value: fmtCurrency(overview.totalSpend || 0) },
    totalImpressions: { icon: Eye, label: 'Impressions', value: fmtNumber(overview.totalImpressions || 0) },
    totalClicks: { icon: MousePointer, label: 'Clicks', value: fmtNumber(overview.totalClicks || 0), sub: `CTR: ${fmtCtr(overview.avgCtr || 0)}` },
    totalConversations: { icon: MessageSquare, label: 'Conversations', value: fmtNumber(overview.totalConversations || 0), sub: overview.avgCostPerConversation ? `CPC: ${fmtCurrency(overview.avgCostPerConversation)}` : undefined },
    totalLeads: { icon: Users, label: 'Leads', value: fmtNumber(overview.totalLeads || 0), sub: overview.avgCostPerLead ? `CPL: ${fmtCurrency(overview.avgCostPerLead)}` : undefined },
    totalPurchases: { icon: TrendingUp, label: 'Purchases', value: fmtNumber(overview.totalPurchases || 0), sub: overview.totalPurchaseValue ? `Rev: ${fmtCurrency(overview.totalPurchaseValue)}` : undefined },
    avgCpc: { icon: DollarSign, label: 'Avg CPC', value: fmtCurrency(overview.avgCpc || 0) },
    avgCtr: { icon: MousePointer, label: 'Avg CTR', value: fmtCtr(overview.avgCtr || 0) },
    activeCampaigns: { icon: Zap, label: 'Active Campaigns', value: String(overview.activeCampaigns || 0), sub: `of ${overview.totalCampaigns || 0} total` },
  }

  const m = metrics[metric] || metrics.totalSpend
  const Icon = m.icon

  return (
    <div className="flex flex-col justify-center h-full px-1">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-primary" />
        <span className="text-xs text-muted-foreground uppercase tracking-wide">{m.label}</span>
      </div>
      <p className="text-2xl font-bold">{m.value}</p>
      {m.sub && <p className="text-xs text-muted-foreground mt-0.5">{m.sub}</p>}
    </div>
  )
}
